/**
 * 工作区卡片组件
 *
 * 显示工作区的封面、名称、进度、统计信息等
 * 支持点击跳转和封面上传功能
 * 使用HTTP REST API进行封面上传
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Workspace } from '../../types/workspace';
import { workspaceApi, HttpError } from '../../services/workspaceApi';
import { formatTokens } from '../../utils/formatters';

// 文件大小限制 (2MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;

// 允许的 MIME 类型
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

// 封面分辨率要求（5:7 比例）
const COVER_WIDTH = 1000;
const COVER_HEIGHT = 1400;
const COVER_TOLERANCE = 50; // 允许±50px 误差
const MIN_WIDTH = COVER_WIDTH - COVER_TOLERANCE;
const MAX_WIDTH = COVER_WIDTH + COVER_TOLERANCE;
const MIN_HEIGHT = COVER_HEIGHT - COVER_TOLERANCE;
const MAX_HEIGHT = COVER_HEIGHT + COVER_TOLERANCE;

// 获取分类显示名称 (移到组件外部提升性能)
const getCategoryLabel = (category: string): string => {
  return category === 'male' ? '男频' : '女频';
};

// 获取图片尺寸
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('无法加载图片'));
    };
    img.src = URL.createObjectURL(file);
  });
};

// 验证文件类型、大小和分辨率
const validateFile = async (file: File): Promise<{ valid: boolean; error: string | null }> => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `文件大小超过限制（最大 2MB），当前文件：${(file.size / 1024 / 1024).toFixed(2)}MB`
    };
  }

  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `不支持的文件类型，仅支持：PNG、JPEG、JPG`
    };
  }

  try {
    const { width, height } = await getImageDimensions(file);
    
    if (width < MIN_WIDTH || width > MAX_WIDTH || height < MIN_HEIGHT || height > MAX_HEIGHT) {
      return {
        valid: false,
        error: `图片尺寸不符合要求，应为${COVER_WIDTH}x${COVER_HEIGHT}像素（允许±${COVER_TOLERANCE}px 误差），当前尺寸：${width}x${height}像素`
      };
    }
  } catch (error) {
    return {
      valid: false,
      error: '无法读取图片尺寸，请重试'
    };
  }

  return { valid: true, error: null };
};

// 进度条子组件
interface ProgressBarProps {
  label: string;
  current: number;
  total: number;
  colorClass: string;
  unit: "万字" | "章";
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  label,
  current,
  total,
  colorClass,
  unit
}) => {
  // 根据单位统一计算百分比
  const percent = useMemo(() => {
    if (unit === "万字") {
      // 字数：current是字，total是万字，需要统一单位
      const totalInWords = total * 10000; // 万字转字
      return Math.round((current / totalInWords) * 100);
    }
    // 章节：直接计算
    return Math.round((current / total) * 100);
  }, [current, total, unit]);

  // 根据单位类型格式化显示值
  const formatCurrent = () => {
    if (unit === "万字") {
      // 字数：转换为万字，保留1位小数
      return (current / 10000).toFixed(1);
    }
    // 章节：直接显示整数
    return current.toString();
  };

  const formatTotal = () => {
    if (unit === "万字") {
      // 目标字数已经是万字单位，直接显示
      return total.toLocaleString();
    }
    // 章节：直接显示整数
    return total.toLocaleString();
  };

  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">
          {formatCurrent()} / {formatTotal()}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
        <div
          className={`${colorClass} h-full transition-all duration-150 rounded-full`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
};

interface WorkspaceCardProps {
  workspace: Workspace;
  onClick: () => void;
  token: string;
  tokenStats?: number; // Token 统计数据（可选）
  onUpdate?: (updatedWorkspace: Workspace) => void;
  onEdit?: (workspace: Workspace) => void;
  onDelete?: (workspace: Workspace) => void;
}

export const WorkspaceCard: React.FC<WorkspaceCardProps> = ({
  workspace,
  onClick,
  token,
  tokenStats,
  onUpdate,
  onEdit,
  onDelete,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 智能生成相关状态
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [userDescription, setUserDescription] = useState('');
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showSubmittedToast, setShowSubmittedToast] = useState(false);

  // 组件卸载时取消未完成的请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 自动关闭提交成功提示
  useEffect(() => {
    if (showSubmittedToast) {
      const timer = setTimeout(() => {
        setShowSubmittedToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSubmittedToast]);

  // 处理封面上传
  const handleUploadCover = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isUploading) {
      return;
    }

    // 重置文件输入，允许重复选择同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // 验证文件（异步验证，包括分辨率检查）
    const validation = await validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    abortControllerRef.current = new AbortController();

    try {
      const coverPath = await workspaceApi.uploadCover(token, workspace.id, file);

      // 通知父组件更新工作区数据
      if (onUpdate && abortControllerRef.current?.signal.aborted === false) {
        onUpdate({
          ...workspace,
          cover_path: coverPath
        });
      }
    } catch (error) {
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (error instanceof HttpError) {
        setUploadError(error.message);
      } else if (error instanceof Error && error.name === 'AbortError') {
        return;
      } else {
        setUploadError('封面上传失败，请重试');
      }
    } finally {
      if (abortControllerRef.current?.signal.aborted === false) {
        setIsUploading(false);
      }
    }
  }, [workspace.id, token, isUploading, onUpdate, workspace]);

  // 处理智能生成封面
  const handleGenerateCover = useCallback(async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    setGenerateError(null);

    try {
      // 调用后端API提交任务
      const result = await workspaceApi.generateCover(
        token,
        workspace.id,
        userDescription || undefined
      );

      // 任务提交成功，关闭对话框并显示提示
      setShowGenerateDialog(false);
      setShowSubmittedToast(true);
      setUserDescription('');

      // 标记已使用智能生成功能
      if (onUpdate) {
        onUpdate({
          ...workspace,
          ai_cover_generated: true
        });
      }
    } catch (error) {
      // 后端返回错误，保持对话框打开，显示错误信息
      if (error instanceof HttpError) {
        setGenerateError(error.message);
      } else {
        setGenerateError('封面生成失败，请重试');
      }
    } finally {
      setIsGenerating(false);
    }
  }, [workspace.id, token, isGenerating, onUpdate, workspace, userDescription]);

  // 触发文件选择器
  const handleUploadClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  // 处理卡片点击（排除上传按钮）
  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const isUploadButton = target.closest('.upload-btn') || target.closest('input[type="file"]');

      if (!isUploadButton) {
        onClick();
      }
    },
    [onClick]
  );

  return (
    <div
      onClick={handleCardClick}
      className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-150 cursor-pointer overflow-hidden"
    >
      {/* 封面区域 */}
      <div className="relative">
        <div className="w-full aspect-[200/260] bg-gray-50 border-b border-gray-100 overflow-hidden flex items-center justify-center">
          {workspace.cover_path ? (
            <img
              src={workspace.cover_path}
              alt={`${workspace.name} 封面`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-gray-400 flex flex-col items-center">
              <svg
                className="w-16 h-16 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <span className="text-sm">暂无封面</span>
            </div>
          )}
        </div>

        {/* 上传和智能生成按钮（悬停时显示） */}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
          {/* 智能生成按钮（仅未使用时显示） */}
          {!workspace.ai_cover_generated && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowGenerateDialog(true);
              }}
              className="upload-btn bg-ink-800 hover:bg-ink-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-150 shadow-xs hover:shadow-sm active:scale-[0.98]"
              disabled={isGenerating}
            >
              {isGenerating ? '生成中...' : '智能生成'}
            </button>
          )}
          {/* 上传按钮 */}
          <button
            onClick={handleUploadClick}
            className="upload-btn bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-150 shadow-xs hover:shadow-sm active:scale-[0.98]"
            disabled={isUploading}
          >
            {isUploading ? '上传中...' : '上传封面'}
          </button>
        </div>

        {/* 隐藏的文件输入 */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleUploadCover}
          className="hidden"
          title={`封面图片要求：${COVER_WIDTH}x${COVER_HEIGHT}像素（允许±${COVER_TOLERANCE}px 误差），PNG/JPG 格式，最大 2MB`}
        />
      </div>

      {/* 工作区名称和分类 */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 flex-1">
            {workspace.name}
          </h3>
          {/* 编辑按钮（悬停时显示） */}
          {onEdit && (
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(workspace);
                }}
                className="p-1.5 bg-gray-100 hover:bg-gray-800 hover:text-white text-gray-600 rounded-lg transition-all duration-150"
                title="编辑"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mb-2">
          <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
            {getCategoryLabel(workspace.category)}
          </span>
          <span className="text-xs text-gray-500 line-clamp-1">{workspace.genre}</span>
        </div>

        {/* 统计信息 */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{workspace.total_chapters || 0} 章</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span>{(workspace.created_words || 0).toLocaleString()} 字</span>
          </div>
        </div>
      </div>

      {/* 上传错误提示 */}
      {uploadError && (
        <div className="mx-4 mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {uploadError}
        </div>
      )}

      {/* 智能生成封面对话框 - 使用 Portal 渲染到 body，避免父元素 transform 影响 */}
      {showGenerateDialog && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => {
            e.stopPropagation();
            setShowGenerateDialog(false);
            setGenerateError(null);
            setUserDescription('');
          }}
        >
          <div
            className="bg-white rounded-2xl p-8 w-96 shadow-sm max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4 text-gray-900">智能生成封面</h3>
            <p className="text-sm text-gray-600 mb-2">
              书名：<span className="font-medium text-gray-900">{workspace.name}</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              系统将根据书名、分类、流派、简介自动生成封面，每个工作区仅可使用一次。
            </p>
            <textarea
              value={userDescription}
              onChange={(e) => setUserDescription(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="可选：输入补充描述，如画面风格、关键元素等..."
              className="w-full border border-gray-200 rounded-lg p-3 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
            />
            {generateError && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                {generateError}
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGenerateDialog(false);
                  setGenerateError(null);
                  setUserDescription('');
                }}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateCover();
                }}
                disabled={isGenerating}
                className="flex-1 py-2 bg-ink-800 text-white rounded-lg hover:bg-ink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? '生成中...' : '开始生成'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 任务提交成功提示 Toast */}
      {showSubmittedToast && createPortal(
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-sm flex items-center gap-2 animate-in slide-in-from-right duration-150">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>任务已提交，可稍后刷新页面</span>
            <button
              onClick={() => setShowSubmittedToast(false)}
              className="ml-2 hover:bg-green-600 rounded p-1 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* 操作按钮 */}
      <div className="px-3 pb-3 space-y-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="w-full bg-ink-800 hover:bg-ink-700 text-white py-2 rounded-lg font-medium text-sm transition-all duration-150 shadow-sm hover:shadow-md active:scale-[0.98]"
        >
          查看详情
        </button>
      </div>
    </div>
  );
};

export default WorkspaceCard;
